import api, { getErrorMessage } from './api';

const budgetsService = {
  /**
   * Get provisioned budget summary (admin only)
   * @returns {Promise<object>} - Provisioned budget info
   */
  getProvisionedBudget: async () => {
    try {
      const response = await api.get('/api/v1/admin/budgets/provision');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get all budgets (admin only)
   * @returns {Promise<Array>} - Budgets list
   */
  getAllBudgets: async () => {
    try {
      const response = await api.get('/api/v1/admin/budgets/');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create budget (admin only)
   * @param {object} budgetData - Budget data
   * @param {string} budgetData.name - Budget name
   * @param {number} budgetData.amount - Budget amount
   * @param {string} budgetData.budget_type - ORGANIZATION or TEAM
   * @param {string} budgetData.organization_id - Organization ID
   * @param {string} budgetData.team_id - Team ID (for team budgets)
   * @param {string} budgetData.admin_user_id - Admin user ID
   * @param {string} budgetData.team_admin_user_id - Team admin user ID (optional)
   * @param {string} budgetData.parent_budget_id - Parent budget ID (for team budgets)
   * @returns {Promise<object>} - Created budget
   */
  createBudget: async (budgetData) => {
    try {
      const response = await api.post('/api/v1/admin/budgets/', budgetData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get budget by ID (admin only)
   * @param {string} budgetId - Budget ID
   * @returns {Promise<object>} - Budget data
   */
  getBudgetById: async (budgetId) => {
    try {
      const response = await api.get(`/api/v1/admin/budgets/${budgetId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update budget (admin only)
   * @param {string} budgetId - Budget ID
   * @param {object} budgetData - Budget data to update
   * @returns {Promise<object>} - Updated budget
   */
  updateBudget: async (budgetId, budgetData) => {
    try {
      const response = await api.put(`/api/v1/admin/budgets/${budgetId}`, budgetData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete budget (admin only)
   * @param {string} budgetId - Budget ID
   * @returns {Promise<object>} - Success message
   */
  deleteBudget: async (budgetId) => {
    try {
      const response = await api.delete(`/api/v1/admin/budgets/${budgetId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get budget usage statistics (admin only)
   * @param {string} budgetId - Budget ID
   * @returns {Promise<object>} - Budget usage stats
   */
  getBudgetUsage: async (budgetId) => {
    try {
      const response = await api.get(`/api/v1/admin/budgets/${budgetId}/usage`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get budget spend log (admin only)
   * @param {string} budgetId - Budget ID
   * @returns {Promise<Array>} - Spend log entries
   */
  getBudgetSpend: async (budgetId) => {
    try {
      const response = await api.get(`/api/v1/admin/budgets/${budgetId}/spend`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get budget reservations (admin only)
   * @param {string} budgetId - Budget ID
   * @returns {Promise<Array>} - Reservation log entries
   */
  getBudgetReservations: async (budgetId) => {
    try {
      const response = await api.get(`/api/v1/admin/budgets/${budgetId}/reservations`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Allocate budget to team (admin only)
   * @param {string} budgetId - Parent budget ID
   * @param {object} allocationData - Allocation data
   * @param {string} allocationData.team_id - Team ID
   * @param {number} allocationData.amount - Allocation amount
   * @param {string} allocationData.name - Custom name (optional)
   * @param {string} allocationData.team_admin_user_id - Team admin ID (optional)
   * @returns {Promise<object>} - Created team budget
   */
  allocateBudget: async (budgetId, allocationData) => {
    try {
      const response = await api.post(`/api/v1/admin/budgets/${budgetId}/allocate`, allocationData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // Alias methods for compatibility with BudgetsSection component
  list: async () => budgetsService.getAllBudgets(),
  create: async (data) => budgetsService.createBudget(data),
  update: async (id, data) => budgetsService.updateBudget(id, data),
  delete: async (id) => budgetsService.deleteBudget(id),
  allocateToTeam: async (budgetId, data) => budgetsService.allocateBudget(budgetId, data),
};

// Aliases for compatibility
budgetsService.list = budgetsService.getAllBudgets;
budgetsService.create = budgetsService.createBudget;
budgetsService.update = budgetsService.updateBudget;
budgetsService.delete = budgetsService.deleteBudget;
budgetsService.allocateToTeam = budgetsService.allocateBudget;
budgetsService.get = budgetsService.getBudgetById;

export default budgetsService;
